
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Plus, Download, Building2,
  Globe, Filter, CheckCircle2, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';

import { getAddressBookList, DeleteCompanyAddress } from '@/redux/Address/actions';
import { callLoader } from '@/redux/Loader/actions';
import Swal from 'sweetalert2';
import Pagination from '@/pages/PortPlace/Pagination';
import { getAddressColumns } from './AddressColumns';
import { DataTable } from '../ui/table';
import { useDebounced } from '@/hooks/useDebounced';

const AddressBook = ({ onCreateNew, onViewShipment }) => {

  const [searchText, setSearchText] = useState('');
  const [searchField, setSearchField] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLetter, setSelectedLetter] = useState();
  const [recordsPerPage, setRecordPerPage] = useState(10);
  const dispatch = useDispatch();

  const { addressList, pagination } = useSelector(state => state.addressBook);

  const debouncedSearch = useDebounced(() => {
    _getList(1);
  }, 300);
  useEffect(() => {
    if (searchText === null) return;
    debouncedSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    _getList(1);
  }, [recordsPerPage, selectedLetter]);
  const changePage = (page) => {
    _getList(page);
  };


  const authUser = JSON.parse(localStorage.getItem('authUser'))?.[0];
  const _getList = async (page = 1) => {
    setCurrentPage(page);
    dispatch(callLoader(1));

    try {
      const payload = {
        IPageNo: page,
        IPageRecords: recordsPerPage,
        SearchText: searchText || "",
        SalesPersonId: 0,
        PrefixLetter: selectedLetter || "",
        compnayId: authUser?.companyId
      };

      await dispatch(getAddressBookList(payload));
    } catch (error) {
      console.error('Error fetching address book list:', error);
      // optional: show toast / alert
    } finally {
      dispatch(callLoader(0));
    }
  };


  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This address will be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (!result.isConfirmed) return;
    const updateDetails = await DeleteCompanyAddress(id);
    if (updateDetails?.statuscode === 201 || updateDetails?.statuscode === 200) {
      toast.success("Address delete successfully");
      _getList(1);
    }
  };

  const alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'ALL'];
  const columns = getAddressColumns({
    onViewShipment,
    handleDelete,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6 pb-12"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-500 via-cyan-600 to-blue-700 rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-lg sm:rounded-xl">
              <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 text-teal-100 text-xs sm:text-sm mb-1">
                <span>Address Book</span>
                <span>/</span>
                <span>Help Link</span>
              </div>
              <h1 className="text-lg sm:text-2xl font-bold text-white">
                Address Book
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onCreateNew}
              className="flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg sm:rounded-xl font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-xs sm:text-sm"
            >
              <Plus className="w-4 h-4" />{" "}
              <span className="hidden xs:inline">Add New</span> Company
            </button>
            <button className="flex-1 sm:flex-none px-3 sm:px-5 py-2 sm:py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg sm:rounded-xl font-medium transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-xs sm:text-sm">
              <Download className="w-4 h-4" /> Export
              <span className="hidden sm:inline"> Data</span>
            </button>
          </div>
        </div>
        {/* Quick Stats */}
        <div className="relative mt-4 sm:mt-6 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          {[
            {
              label: "Total Companies",
              value: pagination?.totalRecord,
              icon: Building2,
              color: "bg-blue-500",
            },
            {
              label: "Active",
              value: addressList.filter((c) => c?.status === "ACTIVE").length,
              icon: CheckCircle2,
              color: "bg-emerald-500",
            },
            {
              label: "Inactive",
              value: addressList.filter((c) => c?.status === "INACTIVE")
                .length,
              icon: XCircle,
              color: "bg-red-500",
            },
            {
              label: "With EDI",
              value: addressList.filter((c) => c?.accountNumber !== "NA")
                .length,
              icon: Globe,
              color: "bg-purple-500",
            },
          ].map((stat, index) => (
            <div
              key={index}
              className={`${stat.color} rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-lg hover:scale-105 transition-transform duration-200`}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-white">
                    {stat.value}
                  </p>
                  <p className="text-[10px] sm:text-xs text-white/80">
                    {stat.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Filters Section */}
      <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col gap-3 sm:gap-4 mb-3 sm:mb-4">
          {/* Filter Dropdown and Search */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">
                Filter:
              </span>
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="px-3 sm:px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {/* Search */}
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Enter text for search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <button className="px-4 sm:px-6 py-2 sm:py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-all flex items-center gap-2 text-xs sm:text-sm">
                <Search className="w-4 h-4" />{" "}
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>
          </div>
        </div>
        {/* Alphabet Filter */}
        <div className="flex flex-wrap items-center gap-1 p-2 sm:p-3 bg-slate-50 dark:bg-slate-800 rounded-lg sm:rounded-xl overflow-x-auto">
          {alphabet.map((letter) => (
            <button
              key={letter}
              onClick={() => {
                setSelectedLetter(letter);
                setCurrentPage(1);
              }}
              className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-semibold transition-all flex-shrink-0 ${selectedLetter === letter
                  ? "bg-teal-600 text-white shadow-md"
                  : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-teal-100 dark:hover:bg-teal-900/30 border border-slate-200 dark:border-slate-600"
                }`}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        data={addressList || []}
        columns={columns}
        boxClass="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl shadow-sm border border-slate-200 dark:border-slate-800"
        trClass={
          "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        }
        thClass={
          "px-3 sm:px-4 py-3 sm:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
        }
        tdClass="px-3 py-3 text-sm text-slate-600 dark:text-slate-400"
        htrClass="bg-gradient-to-r from-slate-700 to-slate-800 text-white"
      />
      <Pagination
        className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8 mb-0 bg-slate-50 dark:bg-slate-800/50 mt-8"
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        setRecordsPerPage={(size) => setRecordPerPage(size)}
        onPrevious={changePage}
        onNext={changePage}
        totalRecord={pagination?.totalRecord}
        handleGoto={changePage}
        title="address book"
      />
    </motion.div>
  );
};

export default AddressBook;